import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { Done, East } from '@mui/icons-material';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';

import dayjs from 'dayjs';
import { Counter } from 'features/common/Counter/Counter';

import { HH_MM_FORMAT, capitalizeFirstLetter, classNames } from '../utils';
import {
  AM_HOURS,
  HOURS,
  MIN_BINS,
  calculateEndHourAndLabel,
  convertTo24HourString,
  getOnlyAmPMHours,
} from './utils';

import styles from './DateTimeSelector.module.css';

interface TimeSelectorProps {
  startHour: string;
  setStartHour: (hour: string) => void;
  endHour: string;
  setEndHour: (hour: string) => void;
  hours: number;
  setHours: React.Dispatch<React.SetStateAction<number>>;
  maxHours: number;
  amPmVersion?: boolean;
  minBins?: boolean;
  canEditEndTime?: boolean;
  onShowHoursChange?: (val: boolean) => void;
  detourVersion?: boolean;
  startExtra?: ReactNode;
  endExtra?: ReactNode;
  endNotBefore?: string;
  getCandidateDurationHours?: (endTime12: string) => number;
}

type TTime = 'start' | 'end';

function DotLineImg({ top = false }: { top?: boolean }) {
  return (
    <div
      className={classNames(styles.dotImg, top ? styles.top : styles.bottom)}
    />
  );
}

export function TimeSelector({
  startHour,
  setStartHour,
  endHour,
  setEndHour,
  hours,
  setHours,
  maxHours,
  amPmVersion = false,
  minBins = false,
  canEditEndTime = false,
  onShowHoursChange,
  detourVersion = false,
  startExtra,
  endExtra,
  endNotBefore,
  getCandidateDurationHours,
}: TimeSelectorProps) {
  const [showHours, setShowHours] = useState<false | TTime>(false);
  const [dayLabel, setDayLabel] = useState<string>('');

  useEffect(() => {
    onShowHoursChange?.(Boolean(showHours));
  }, [showHours, onShowHoursChange]);

  useEffect(() => {
    if (!startHour) return;
    const { endHour: computedEndHour, dayLabel: computedLabel } =
      calculateEndHourAndLabel(startHour, hours);
    setEndHour(computedEndHour);
    setDayLabel(computedLabel);
  }, [startHour, hours]);

  const handleHourClick = (h?: string, m?: string, meridian?: string) => {
    if (!showHours) return;
    let hour: string;
    let updateHour: (h: string) => void;
    if (showHours === 'start') {
      hour = startHour;
      updateHour = setStartHour;
    } else if (showHours === 'end') {
      hour = endHour;
      updateHour = setEndHour;
    } else return;

    const [time, period] = hour.split(' ');
    const [currHour, currMinute] = time.split(':');
    const newHour = h ?? currHour;
    const newMinute = m ?? currMinute;
    const newPeriod = meridian ?? period;
    updateHour(`${newHour}:${newMinute} ${newPeriod}`);
    if (!amPmVersion) setShowHours(false);
  };

  const toggleShowHours = useCallback((val: TTime) => {
    setShowHours((prev) => (prev ? false : val));
  }, []);

  const handleCountChange = (val: number) => {
    const fraction = hours % 1;
    const newHours = Math.min(maxHours, val + fraction);
    setHours((prev) => (prev !== newHours ? newHours : prev));
  };

  const handleMinsChange = (val: number) => {
    const newHours = Math.min(maxHours, Math.floor(hours) + val / 60);
    setHours((prev) => (prev !== newHours ? newHours : prev));
  };

  const formattedStartHour = useMemo(
    () => (minBins ? startHour : getOnlyAmPMHours(startHour)),
    [minBins, startHour],
  );

  const formattedEndHour = useMemo(
    () => (minBins ? endHour : getOnlyAmPMHours(endHour)),
    [minBins, endHour],
  );

  const shouldShowEnd = showHours !== 'start';
  const shouldShowStart = showHours !== 'end';
  const headerSize = detourVersion ? 7 : showHours && 12;

  const startHeader = (
    <HourHeader
      showHours={showHours}
      handleClick={toggleShowHours}
      amPmVersion={amPmVersion}
      size={headerSize}
      formattedHour={formattedStartHour}
      type="start"
      hidden={!shouldShowStart}
    />
  );

  const endHeader = (
    <HourHeader
      showHours={showHours}
      handleClick={toggleShowHours}
      amPmVersion={amPmVersion}
      size={headerSize}
      formattedHour={formattedEndHour}
      type="end"
      hidden={!shouldShowEnd}
      dayLabel={!detourVersion && dayLabel}
      disabled={!canEditEndTime}
    />
  );

  return (
    <Grid
      container
      spacing={1}
      className={classNames(styles.time_picker, detourVersion && styles.detour)}
      columns={{ xs: 12 }}
    >
      {!detourVersion ? (
        <Grid item xs={12}>
          {startHeader}
          <ArrowIcon hidden={Boolean(showHours)} />
          {endHeader}
        </Grid>
      ) : (
        shouldShowStart && (
          <Grid item xs={12} className={styles.startDetour}>
            <Grid item xs={5}>
              {startExtra}
            </Grid>
            {startHeader}
          </Grid>
        )
      )}
      {showHours ? (
        <HoursMenu
          handleHourClick={handleHourClick}
          amPmVersion={amPmVersion}
          showHours={showHours}
          startHour={startHour}
          endHour={endHour}
          endNotBefore={endNotBefore}
          maxHours={maxHours}
          getCandidateDurationHours={getCandidateDurationHours}
        />
      ) : (
        <DurationComponent
          detourVersion={detourVersion}
          minBins={minBins}
          hours={hours}
          maxHours={maxHours}
          handleCountChange={handleCountChange}
          handleMinsChange={handleMinsChange}
        />
      )}
      {detourVersion && shouldShowEnd && (
        <Grid item xs={12} className={styles.endDetour}>
          <Grid item xs={5}>
            {endExtra}
          </Grid>
          {endHeader}
        </Grid>
      )}
    </Grid>
  );
}

function HoursMenu({
  amPmVersion,
  showHours,
  startHour,
  endHour,
  handleHourClick,
  endNotBefore,
  maxHours,
  getCandidateDurationHours,
}: {
  startHour: string;
  endHour: string;
  amPmVersion: boolean;
  showHours: TTime;
  handleHourClick: any;
  endNotBefore?: string;
  maxHours: number;
  getCandidateDurationHours?: (endTime12: string) => number;
}) {
  const selectableHours: string[] = amPmVersion ? AM_HOURS : HOURS;

  const currTime = showHours === 'start' ? startHour : endHour;

  function isCurrent(val: string) {
    const formatted = amPmVersion ? currTime : getOnlyAmPMHours(currTime);
    return formatted.includes(val);
  }

  const shouldShowStart = showHours !== 'end';

  const isDisabled = (val: string) => {
    if (isCurrent(val)) return true;
    if (shouldShowStart) return false;

    const [time, period] = currTime.split(' ');
    const [currHour, currMinute] = time.split(':');
    const candidate = val.startsWith(':')
      ? `${currHour}:${val.slice(1)} ${period}`
      : val === 'AM' || val === 'PM'
        ? `${currHour}:${currMinute} ${val}`
        : `${val}:${currMinute} ${period}`;

    let isEndAtOrBeforeStart = false;
    if (endNotBefore) {
      const start = dayjs(convertTo24HourString(endNotBefore), HH_MM_FORMAT);
      const end = dayjs(convertTo24HourString(candidate), HH_MM_FORMAT);
      isEndAtOrBeforeStart = !end.isAfter(start);
    }

    if (getCandidateDurationHours)
      return (
        isEndAtOrBeforeStart || getCandidateDurationHours(candidate) > maxHours
      );

    return isEndAtOrBeforeStart;
  };

  return (
    <Stack className={styles.hoursMenu}>
      <Stack className={styles.innerWrapper}>
        {amPmVersion && (
          <Typography
            variant="caption"
            className={styles.time_selector_typography}
          >
            Hour
          </Typography>
        )}
        <Grid container className={styles.gridContainer}>
          {selectableHours.map((h) => (
            <Grid item key={h} xs={3}>
              <Button
                className={styles.hour_button}
                id={isCurrent(h) ? styles.button_active : undefined}
                disabled={isDisabled(h)}
                onClick={() =>
                  handleHourClick(h.split(' ')[0], undefined, h.split(' ')[1])
                }
              >
                <Typography variant="caption">{h}</Typography>
              </Button>
            </Grid>
          ))}
        </Grid>
      </Stack>
      {amPmVersion && (
        <>
          <Stack className={styles.innerWrapper}>
            <Typography
              variant="caption"
              className={styles.time_selector_typography}
            >
              Minutes
            </Typography>
            <Grid container className={styles.gridContainer}>
              {MIN_BINS.map((m) => (
                <Grid item key={m} xs={3}>
                  <Button
                    className={styles.hour_button}
                    id={isCurrent(`:${m}`) ? styles.button_active : undefined}
                    disabled={isDisabled(`:${m}`)}
                    onClick={() => handleHourClick(undefined, m, undefined)}
                  >
                    <Typography variant="caption">{m}</Typography>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Stack>
          <Stack className={styles.innerWrapper}>
            <Typography
              variant="caption"
              className={styles.time_selector_typography}
            >
              AM / PM
            </Typography>
            <Grid container className={styles.gridContainer}>
              {['AM', 'PM'].map((el) => (
                <Grid item key={el} xs={6}>
                  <Button
                    className={styles.hour_button}
                    id={isCurrent(el) ? styles.button_active : undefined}
                    disabled={isDisabled(el)}
                    onClick={() => handleHourClick(undefined, undefined, el)}
                  >
                    <Typography variant="caption">{el}</Typography>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </>
      )}
    </Stack>
  );
}

export function ArrowIcon({ hidden }: { hidden?: boolean }) {
  if (hidden) return null;
  return (
    <Grid item xs={1.5} className={styles.arrow_icon}>
      <East />
    </Grid>
  );
}

function ConfirmButton({
  hidden,
  onClick,
}: {
  hidden?: boolean;
  onClick: () => void;
}) {
  if (hidden) return null;
  return (
    <IconButton
      id={styles.confirm_button}
      size="small"
      edge="end"
      title="Confirm"
      onClick={onClick}
    >
      <Done />
    </IconButton>
  );
}

interface IHourHeader {
  hidden: boolean;
  formattedHour: string;
  amPmVersion: boolean;
  handleClick?: (any) => void;
  showHours: TTime | false;
  type: TTime;
  dayLabel?: string;
  disabled?: boolean;
  size?: number;
}

function HourHeader({
  hidden,
  showHours,
  type,
  handleClick,
  amPmVersion,
  formattedHour,
  dayLabel,
  disabled = false,
  size = 5.5,
}: IHourHeader) {
  if (hidden) return null;
  return (
    <Grid item xs={size} className={styles.header_item}>
      <Typography variant="caption" className={styles.time_selector_typography}>
        {capitalizeFirstLetter(type)}{' '}
        {dayLabel && <span className={styles.day_label}>({dayLabel})</span>}
      </Typography>
      <Box display="flex" gap={0.5}>
        <Button
          className={styles.displayBtn}
          disabled={disabled}
          onClick={() => handleClick(type)}
          id={showHours === type ? styles.open : undefined}
        >
          <Typography variant="caption">{formattedHour}</Typography>
        </Button>
        <ConfirmButton
          hidden={!(amPmVersion && showHours === type)}
          onClick={() => handleClick(type)}
        />
      </Box>
    </Grid>
  );
}

type DurationComponentProps = {
  detourVersion: boolean;
  minBins: boolean;
  hours: number;
  maxHours: number;
  handleCountChange: (value: number) => void;
  handleMinsChange: (value: number) => void;
};

const DurationComponent = ({
  detourVersion,
  minBins,
  hours,
  maxHours,
  handleCountChange,
  handleMinsChange,
}: DurationComponentProps) => {
  const minHours = minBins && hours % 1 !== 0 ? 0 : 1;

  const content = (
    <Stack direction="column" className={styles.duration_mins_container}>
      <Typography variant="caption">Duration</Typography>

      <Counter
        allowInput
        txt="hour"
        max={maxHours}
        min={minHours}
        defaultState={Math.floor(hours)}
        onCountChange={handleCountChange}
      />

      {minBins && (
        <Counter
          allowInput
          txt="min"
          incrementStep={15}
          max={
            Math.floor(hours) === maxHours
              ? parseInt(MIN_BINS[0], 10)
              : parseInt(MIN_BINS.at(-1), 10)
          }
          min={0}
          defaultState={parseInt(MIN_BINS[(hours % 1) * 4], 10)}
          onCountChange={handleMinsChange}
        />
      )}
    </Stack>
  );

  if (!detourVersion) return content;

  return (
    <div>
      <Divider />
      <DotLineImg top />
      {content}
      <DotLineImg />
      <Divider />
    </div>
  );
};
